import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { Loader2, CreditCard, Trash2 } from "lucide-react";

const cy = (c) => (c === "MYR" ? "RM" : (c || "RM") + " ");
const productLabel = (p) => p.product_label || (p.product === "featured_race" || p.type === "featured" ? "Featured Race" : "Race Listing");

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fProduct, setFProduct] = useState("all");
  const [fCurrency, setFCurrency] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");

  const load = () => { setLoading(true); api.get("/admin/payments").then(({ data }) => setRows(data.payments)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    const msg = p.is_demo ? `Delete this demo payment (${p.reference})?`
      : `Delete this REAL payment (${p.reference}) for ${cy(p.currency)}${p.amount}?\n\nThis permanently removes the transaction record.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/payments/${p.id}`); toast.success("Payment deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const filtered = useMemo(() => rows.filter((p) => {
    const prod = p.product || (p.type === "featured" ? "featured_race" : "race_listing");
    if (fProduct !== "all" && prod !== fProduct) return false;
    if (fCurrency !== "all" && (p.currency || "MYR") !== fCurrency) return false;
    if (fStatus !== "all" && p.status !== fStatus) return false;
    if (search && !`${p.organizer_name} ${p.race_name} ${p.reference} ${p.invoice_number || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, fProduct, fCurrency, fStatus, search]);

  const revByCur = useMemo(() => {
    const m = {};
    filtered.filter((p) => p.status === "paid" && !p.is_demo).forEach((p) => { const c = p.currency || "MYR"; m[c] = (m[c] || 0) + Number(p.amount || 0); });
    return m;
  }, [filtered]);

  return (
    <div>
      <AdminHeader title="Payments" subtitle="All transactions — listing & featured." />
      <div className="flex gap-3 flex-wrap mb-4">
        {Object.keys(revByCur).length === 0 ? (
          <div className="bg-[#0a1420] text-white rounded-xl p-5 inline-flex items-center gap-3"><CreditCard className="text-[#22d3ee]" /><div><div className="font-display font-black text-2xl font-mono-data">RM0</div><div className="text-sm text-slate-300">Revenue (filtered)</div></div></div>
        ) : Object.entries(revByCur).map(([c, v]) => (
          <div key={c} className="bg-[#0a1420] text-white rounded-xl p-5 inline-flex items-center gap-3" data-testid={`revenue-${c}`}><CreditCard className="text-[#22d3ee]" /><div><div className="font-display font-black text-2xl font-mono-data">{cy(c)}{v.toLocaleString()}</div><div className="text-sm text-slate-300">{c} revenue (filtered)</div></div></div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organizer, race, ref, invoice…" data-testid="payments-search" className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-64 outline-none focus:border-[#22d3ee]" />
        <select value={fProduct} onChange={(e) => setFProduct(e.target.value)} data-testid="filter-product" className="border border-slate-300 rounded-lg px-3 py-2 text-sm"><option value="all">All products</option><option value="race_listing">Race Listing</option><option value="featured_race">Featured Race</option></select>
        <select value={fCurrency} onChange={(e) => setFCurrency(e.target.value)} data-testid="filter-currency" className="border border-slate-300 rounded-lg px-3 py-2 text-sm"><option value="all">All currencies</option><option value="MYR">MYR</option><option value="SGD">SGD</option></select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status" className="border border-slate-300 rounded-lg px-3 py-2 text-sm"><option value="all">All statuses</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option></select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="admin-payments-table">
        {loading ? <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" /></div> :
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left p-3">Date</th><th className="text-left p-3">Organizer</th><th className="text-left p-3">Race</th><th className="text-left p-3">Product</th><th className="text-left p-3">Duration</th><th className="text-left p-3">Country</th><th className="text-right p-3">Amount</th><th className="text-left p-3">Invoice</th><th className="text-right p-3">Status</th><th className="text-right p-3"></th>
            </tr></thead>
            <tbody>{filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                <td className="p-3 text-slate-600">{(p.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-slate-800"><div className="flex items-center gap-2">{p.organizer_name}{p.is_demo && <span className="text-[10px] font-bold bg-[#22d3ee] text-[#0a1420] px-1.5 py-0.5 rounded">DEMO</span>}</div></td>
                <td className="p-3 text-slate-600">{p.race_name}</td>
                <td className="p-3 text-slate-600">{productLabel(p)}</td>
                <td className="p-3 text-slate-500">{p.duration_label || (p.duration_months ? `${p.duration_months} mo` : "—")}</td>
                <td className="p-3 text-slate-500">{p.billing_country || "—"}</td>
                <td className="p-3 text-right font-mono-data">{cy(p.currency)}{p.amount}</td>
                <td className="p-3 text-slate-500 font-mono-data">{p.invoice_number || "—"}</td>
                <td className="p-3 text-right"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                <td className="p-3 text-right"><button onClick={() => del(p)} className="p-2 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500 inline-flex" title="Delete payment" data-testid={`delete-payment-${p.id}`}><Trash2 size={15} /></button></td>
              </tr>
            ))}</tbody>
          </table>}
        {!loading && filtered.length === 0 && <p className="p-8 text-center text-slate-500">No payments match these filters.</p>}
      </div>
    </div>
  );
}
